import { useEffect, useId, useState } from 'react';
import { api, wordpressApi } from '../../api';
import type { Server, WordPressDiscovery } from '../../api';
import { ErrorNotice, PasswordInput } from '../ui';
import { Field, FieldError, Input, OptionCard, Radio, Select, Textarea } from '../primitives';
import { OnboardingChoice } from '../OnboardingLayout';

/** Validation messages from the page, shown where each one is fixed. */
export interface WordPressTargetErrors {
  /** No server chosen or connected. */
  server?: string;
  /** No installation chosen, or not an absolute path. */
  root?: string;
}

/** What the picker produces: the server and the directory WP-CLI is pointed at. */
export interface WordPressTarget {
  serverId: string;
  rootPath: string;
  releasesRoot: string;
}

/**
 * WordPressSitePicker is the WordPress half of onboarding: reach the
 * hosting account (an agent server, or SSH where no agent can be
 * installed), find the WordPress installations on it, and choose one.
 * Discovery only reads — it lists directories that contain wp-load.php and
 * changes nothing on the server.
 */
export function WordPressSitePicker({
  value,
  onChange,
  errors,
}: {
  value: WordPressTarget;
  onChange: (next: WordPressTarget) => void;
  errors?: WordPressTargetErrors;
}) {
  const id = useId();
  const [servers, setServers] = useState<Server[] | null>(null);
  const [serversError, setServersError] = useState<Error | null>(null);
  const [method, setMethod] = useState<'existing' | 'ssh'>('existing');

  const [discovery, setDiscovery] = useState<WordPressDiscovery | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<Error | null>(null);
  const [manualRoot, setManualRoot] = useState(false);
  // On an agent server the agent searches as the owner of this directory,
  // never as root, so the operator names where to look. Over SSH the
  // account's home directory is searched.
  const [searchRoot, setSearchRoot] = useState('');

  useEffect(() => {
    let alive = true;
    api
      .listServers()
      .then((res) => {
        if (!alive) return;
        const list = res.servers ?? [];
        setServers(list);
        if (list.length === 0) setMethod('ssh');
      })
      .catch((err) => alive && setServersError(err instanceof Error ? err : new Error(String(err))));
    return () => {
      alive = false;
    };
  }, []);

  function chooseServer(serverId: string) {
    setDiscovery(null);
    setDiscoverError(null);
    onChange({ ...value, serverId, rootPath: '' });
  }

  async function discover() {
    if (!value.serverId) return;
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const found = await wordpressApi.discover(value.serverId, viaAgent ? searchRoot.trim() : undefined);
      setDiscovery(found);
      const sites = found.sites ?? [];
      if (sites.length === 1 && sites[0]) onChange({ ...value, rootPath: sites[0].root });
      if (sites.length === 0) setManualRoot(true);
    } catch (err) {
      setDiscoverError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setDiscovering(false);
    }
  }

  const sites = discovery?.sites ?? [];
  const selectedServer = servers?.find((server) => server.id === value.serverId);
  const viaAgent = Boolean(selectedServer) && selectedServer?.access_kind !== 'ssh';
  const showRootInput = manualRoot || Boolean(discovery && sites.length === 0);

  return (
    <div className="wp-picker">
      <OnboardingChoice label="How Commerce Ops reaches the hosting account">
        <OptionCard
          title="A server already in Commerce Ops"
          selected={method === 'existing'}
          selectedLabel="Selected"
          onSelect={() => setMethod('existing')}
          disabled={servers !== null && servers.length === 0}
        />
        <OptionCard
          title="Connect over SSH"
          description="For hosting without an agent. Only fixed WP-CLI operations run."
          selected={method === 'ssh'}
          selectedLabel="Selected"
          onSelect={() => setMethod('ssh')}
        />
      </OnboardingChoice>

      <ErrorNotice error={serversError} />

      {method === 'existing' ? (
        <Field id={`${id}-server`} label="Server" error={errors?.server}>
          <Select
            value={value.serverId}
            onChange={(event) => chooseServer(event.target.value)}
            data-testid="wp-server-select"
          >
            <option value="">{servers === null ? 'Loading servers…' : 'Choose a server'}</option>
            {(servers ?? []).map((server) => (
              <option key={server.id} value={server.id}>
                {server.name} — {server.access_kind === 'ssh' ? 'SSH' : 'agent'}
                {server.hostname ? ` (${server.hostname})` : ''}
              </option>
            ))}
          </Select>
        </Field>
      ) : (
        <SSHConnectForm
          error={errors?.server}
          onConnected={(server) => {
            setServers((list) => [server, ...(list ?? []).filter((item) => item.id !== server.id)]);
            setMethod('existing');
            chooseServer(server.id);
          }}
        />
      )}

      {value.serverId && method === 'existing' ? (
        <div className="wp-picker__discover" data-onboarding-invalid={errors?.root && !showRootInput && sites.length === 0 ? 'true' : undefined}>
          <div className="wp-picker__discover-head">
            <div>
              <strong id={`${id}-sites`}>WordPress installation</strong>
              <p className="muted wp-picker__hint">
                {discovery
                  ? `Searched ${discovery.home || 'the account'} as ${discovery.user || 'the SSH user'} · ${discovery.wp_cli ? `WP-CLI ${discovery.wp_cli}` : 'WP-CLI not found'}`
                  : viaAgent
                    ? 'Searched as the directory’s owner, never root. Only directory names are read.'
                    : 'Only directory names are read.'}
              </p>
            </div>
            <button
              type="button"
              className="button button--secondary"
              onClick={discover}
              disabled={discovering || (viaAgent && !searchRoot.trim().startsWith('/'))}
              data-testid="wp-discover"
            >
              {discovering ? 'Searching' : discovery ? 'Search again' : 'Find WordPress sites'}
            </button>
          </div>
          {viaAgent ? (
            <Field label="Search in" hint="Absolute path of a directory owned by the site’s user.">
              <Input
                value={searchRoot}
                onChange={(event) => setSearchRoot(event.target.value)}
                placeholder="/var/www"
                spellCheck={false}
                autoComplete="off"
                data-testid="wp-search-root"
              />
            </Field>
          ) : null}
          <ErrorNotice error={discoverError} />

          {sites.length > 0 ? (
            <div
              role="radiogroup"
              aria-labelledby={`${id}-sites`}
              className="wp-picker__sites"
              data-onboarding-invalid={errors?.root && !showRootInput ? 'true' : undefined}
            >
              {sites.map((site) => (
                <Radio
                  key={site.root}
                  name={`${id}-root`}
                  checked={!manualRoot && value.rootPath === site.root}
                  onChange={() => {
                      setManualRoot(false);
                      onChange({ ...value, rootPath: site.root });
                    }}
                  label={
                    <>
                      <code>{site.root}</code>
                      {site.has_config ? null : (
                        <span className="muted"> — no wp-config.php beside it</span>
                      )}
                    </>
                  }
                />
              ))}
              <Radio
                name={`${id}-root`}
                checked={manualRoot}
                onChange={() => setManualRoot(true)}
                label="Another directory"
              />
            </div>
          ) : discovery ? (
            <p className="muted">No WordPress installation found</p>
          ) : null}

          {showRootInput ? (
            <Field
              id={`${id}-root-path`}
              label="WordPress directory"
              hint="The absolute path that holds wp-load.php (for Bedrock, the web/wp directory)."
              error={errors?.root}
            >
              <Input
                value={value.rootPath}
                onChange={(event) => onChange({ ...value, rootPath: event.target.value.trim() })}
                placeholder="/home/shop/public_html"
                spellCheck={false}
                data-testid="wp-root-input"
              />
            </Field>
          ) : (
            <FieldError message={errors?.root} />
          )}

          {value.rootPath ? (
            <Field
              label="Where Commerce Ops keeps releases and checkpoints"
              hint="Optional. Kept outside the public web root. Empty: a commerce-ops directory beside the site."
            >
              <Input
                value={value.releasesRoot}
                onChange={(event) => onChange({ ...value, releasesRoot: event.target.value.trim() })}
                placeholder="/home/shop/commerce-ops/production"
                spellCheck={false}
              />
            </Field>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SSHConnectForm({ onConnected, error: pageError }: { onConnected: (server: Server) => void; error?: string }) {
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [username, setUsername] = useState('');
  const [authKind, setAuthKind] = useState<'ssh_password' | 'ssh_private_key'>('ssh_private_key');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const ready = host.trim() !== '' && username.trim() !== '' && secret !== '' && Number(port) > 0;

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const server = await wordpressApi.connectSSHServer({
        name: name.trim() || `${username.trim()}@${host.trim()}`,
        host: host.trim(),
        port: Number(port) || 22,
        username: username.trim(),
        auth_kind: authKind,
        secret,
      });
      setSecret('');
      onConnected(server);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  // Not a <form>: the picker usually sits inside the page's own form, and
  // nested forms are invalid HTML. Enter in a field connects, and never
  // submits the page's form around it.
  return (
    <div
      className="wp-picker__ssh"
      data-onboarding-invalid={pageError ? 'true' : undefined}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.target as HTMLElement).tagName === 'INPUT') {
          event.preventDefault();
          if (ready && !busy) void connect();
        }
      }}
    >
      <div className="wp-picker__row">
        <Field label="Host">
          <Input value={host} onChange={(event) => setHost(event.target.value)} placeholder="ssh.example-host.com" spellCheck={false} autoComplete="off" data-testid="wp-ssh-host" />
        </Field>
        <Field label="Port">
          <Input value={port} onChange={(event) => setPort(event.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" className="wp-picker__port" />
        </Field>
      </div>
      <Field label="Username">
        <Input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="off" spellCheck={false} data-testid="wp-ssh-user" />
      </Field>
      <Field label="Sign in with">
        <Select value={authKind} onChange={(event) => setAuthKind(event.target.value as typeof authKind)}>
          <option value="ssh_private_key">Private key</option>
          <option value="ssh_password">Password</option>
        </Select>
      </Field>
      {authKind === 'ssh_private_key' ? (
        <Field label="Private key" hint="Stored encrypted and never shown again. Use a key for this account only.">
          <Textarea
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
            rows={4}
            spellCheck={false}
            autoComplete="off"
            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
            className="wp-picker__key"
          />
        </Field>
      ) : (
        <Field label="Password" hint="Stored encrypted and never shown again.">
          <PasswordInput value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="off" required={false} />
        </Field>
      )}
      <Field label="Name in Commerce Ops" hint="Optional. Defaults to user@host.">
        <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={username && host ? `${username}@${host}` : 'Shop hosting'} />
      </Field>
      <p className="muted wp-picker__hint">
        The server’s host key is recorded on this first connection; a different key later is refused.
      </p>
      <ErrorNotice error={error} />
      <FieldError message={pageError} />
      <div>
        <button type="button" className="button button--secondary" onClick={connect} disabled={!ready || busy} data-testid="wp-ssh-connect">
          {busy ? 'Connecting' : 'Connect'}
        </button>
      </div>
    </div>
  );
}
